import { isDefined } from "remeda";
// src/common/constants/checkout-preview.ts
const CHECKOUT_PREVIEW_STATUSES = [
	"new",
	"details",
	"paid",
	"complete",
	"expired",
	"invalid",
	"refunded",
	"refund-unclaimed",
	"refund-pending",
	"refund-failed",
	"unknown",
	"unavailable"
];
const DEFAULT_CHECKOUT_PREVIEW_STATUS = "new";
const CHECKOUT_PREVIEW_RECEIVED = [
	"none",
	"partial",
	"partial-2",
	"full",
	"full-2",
	"over"
];
const CHECKOUT_PREVIEW_METHODS = [
	"btc",
	"lightning",
	"eth",
	"usdt"
];
const CHECKOUT_PREVIEW_EXPIRATIONS = [
	"15m",
	"10m-left",
	"6m-left",
	"5m-left",
	"30s-left",
	"2h",
	"2d",
	"6d",
	"7d"
];
const CHECKOUT_PREVIEW_CASES = {
	new: { status: "new" },
	expiring: {
		status: "new",
		expiration: "30s-left"
	},
	lightning: {
		status: "new",
		method: "lightning"
	},
	"zero-price": {
		status: "new",
		anyAmount: true
	},
	discount: {
		status: "new",
		discount: true
	},
	"network-fee": {
		status: "new",
		fee: true
	},
	details: { status: "details" },
	"sender-address": {
		status: "new",
		method: "eth",
		senderAddress: true
	},
	partial: {
		status: "new",
		received: "partial"
	},
	"partial-split": {
		status: "new",
		received: "partial-2",
		fee: true
	},
	"partial-split-discount": {
		status: "new",
		received: "partial-2",
		fee: true,
		discount: true
	},
	unavailable: { status: "unavailable" },
	paid: { status: "paid" },
	complete: { status: "complete" },
	"complete-split": {
		status: "complete",
		received: "full-2"
	},
	"complete-lightning": {
		status: "complete",
		method: "lightning"
	},
	"complete-discount": {
		status: "complete",
		discount: true,
		fee: true
	},
	"complete-zero-price": {
		status: "complete",
		received: "over",
		anyAmount: true
	},
	overpaid: {
		status: "complete",
		received: "over"
	},
	expired: { status: "expired" },
	"expired-partial": {
		status: "expired",
		received: "partial"
	},
	invalid: { status: "invalid" },
	"failed-confirm": {
		status: "invalid",
		received: "full"
	},
	refunded: { status: "refunded" },
	"refund-unclaimed": { status: "refund-unclaimed" },
	"refund-pending": { status: "refund-pending" },
	"refund-failed": { status: "refund-failed" },
	unknown: { status: "unknown" }
};
const CHECKOUT_PREVIEW_CASE_IDS = Object.keys(CHECKOUT_PREVIEW_CASES);
const checkoutPreviewCaseQuery = (previewCase) => new URLSearchParams(Object.entries(previewCase).map(([axis, value]) => [axis, String(value)])).toString();
const CHECKOUT_PREVIEW_VIEWS = ["checkout", "receipt"];
const CHECKOUT_PREVIEW_ENVIRONMENTS = [
	"terminal",
	"terminal-out-of-paper",
	"terminal-no-printer"
];
const CHECKOUT_PREVIEW_WALLETS = [
	"one",
	"two",
	"rejects",
	"wrong-network",
	"low-balance",
	"no-gas",
	"busy",
	"fails"
];
// src/common/constants/test-ids.ts
//! Maintain alphabetical order
const CHECKOUT_AMOUNT_BREAKDOWN_TESTID = "checkout-amount-breakdown";
const CHECKOUT_AMOUNT_TESTID = "checkout-amount";
const CHECKOUT_CLOSE_TESTID = "checkout-close";
const CHECKOUT_COUNTDOWN_TESTID = "checkout-countdown";
const CHECKOUT_METHOD_HINT_TESTID = "checkout-method-hint";
const CHECKOUT_METHOD_SELECTOR_TESTID = "checkout-method-selector";
const CHECKOUT_OPEN_WALLET_TESTID = "checkout-open-wallet";
const CHECKOUT_PAYMENT_ADDRESS_TESTID = "checkout-payment-address";
const CHECKOUT_PAYMENT_QR_TESTID = "checkout-payment-qr";
const CHECKOUT_RECEIPT_TESTID = "checkout-receipt";
const CHECKOUT_RECOMMENDED_FEE_TESTID = "checkout-recommended-fee";
const CHECKOUT_REFUND_TESTID = "checkout-refund";
const CHECKOUT_REQUIRED_CONTENT_GUARD_TESTID = "checkout-required-content-guard";
const CHECKOUT_SENDER_ADDRESS_TESTID = "checkout-sender-address";
const CHECKOUT_STATUS_TESTID = "checkout-status";
const CHECKOUT_WALLET_SENT_TESTID = "checkout-wallet-sent";
const UI_THEME_ICON_DARK_TESTID = "ui-theme-icon-dark";
const UI_THEME_ICON_LIGHT_TESTID = "ui-theme-icon-light";
const UI_THEME_ICON_SYSTEM_TESTID = "ui-theme-icon-system";
const UI_THEME_MOBILE_TOGGLE_TESTID = "ui-theme-mobile-toggle";
const UI_THEME_TOGGLE_TESTID = "ui-theme-toggle";
const GLOBAL_THEME_OVERRIDE_TESTID = "global-theme-override";
const LAYOUT_CONTAINER_TESTID = "layout-container";
const LOCALE_SELECTOR_TRIGGER_TESTID = "locale-selector-trigger";
const MOBILE_MENU_CONTENT_TESTID = "mobile-menu-content";
const MOBILE_MENU_TOGGLE_TESTID = "mobile-menu-toggle";
const NAVBAR_TESTID = "navbar";
const NAV_DROPDOWN_CONTENT_TESTID = "nav-dropdown-content";
const NAV_DROPDOWN_TOGGLE_TESTID = "nav-dropdown-toggle";
// src/common/utils/colors.ts
const extractHexAnnotatedOklchTriplets = (text) => text.matchAll(/"(?<triplet>[\d.% ]+)", \/\/\s+(?<hex>#[\dA-Fa-f]{6})/gu).flatMap(({ groups }) => isDefined(groups?.triplet) && isDefined(groups.hex) ? [[groups.triplet, groups.hex]] : []).toArray();
export { CHECKOUT_AMOUNT_BREAKDOWN_TESTID, CHECKOUT_AMOUNT_TESTID, CHECKOUT_CLOSE_TESTID, CHECKOUT_COUNTDOWN_TESTID, CHECKOUT_METHOD_HINT_TESTID, CHECKOUT_METHOD_SELECTOR_TESTID, CHECKOUT_OPEN_WALLET_TESTID, CHECKOUT_PAYMENT_ADDRESS_TESTID, CHECKOUT_PAYMENT_QR_TESTID, CHECKOUT_PREVIEW_CASES, CHECKOUT_PREVIEW_CASE_IDS, CHECKOUT_PREVIEW_ENVIRONMENTS, CHECKOUT_PREVIEW_EXPIRATIONS, CHECKOUT_PREVIEW_METHODS, CHECKOUT_PREVIEW_RECEIVED, CHECKOUT_PREVIEW_STATUSES, CHECKOUT_PREVIEW_VIEWS, CHECKOUT_PREVIEW_WALLETS, CHECKOUT_RECEIPT_TESTID, CHECKOUT_RECOMMENDED_FEE_TESTID, CHECKOUT_REFUND_TESTID, CHECKOUT_REQUIRED_CONTENT_GUARD_TESTID, CHECKOUT_SENDER_ADDRESS_TESTID, CHECKOUT_STATUS_TESTID, CHECKOUT_WALLET_SENT_TESTID, DEFAULT_CHECKOUT_PREVIEW_STATUS, GLOBAL_THEME_OVERRIDE_TESTID, LAYOUT_CONTAINER_TESTID, LOCALE_SELECTOR_TRIGGER_TESTID, MOBILE_MENU_CONTENT_TESTID, MOBILE_MENU_TOGGLE_TESTID, NAVBAR_TESTID, NAV_DROPDOWN_CONTENT_TESTID, NAV_DROPDOWN_TOGGLE_TESTID, UI_THEME_ICON_DARK_TESTID, UI_THEME_ICON_LIGHT_TESTID, UI_THEME_ICON_SYSTEM_TESTID, UI_THEME_MOBILE_TOGGLE_TESTID, UI_THEME_TOGGLE_TESTID, checkoutPreviewCaseQuery, extractHexAnnotatedOklchTriplets };

//# sourceMappingURL=index.js.map